import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { settingsAPI, mapsAPI, pricingAPI, bankAccountsAPI, cardTerminalsAPI, adminAPI, manualsAPI } from "../lib/api";
import { formatDateTime } from "../lib/formatters";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import VehicleTypesManager from "../components/VehicleTypesManager";
import ServiceTypesManager from "../components/ServiceTypesManager";
import ExpenseTypesManager from "../components/ExpenseTypesManager";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Settings,
  MessageSquare,
  CreditCard,
  MapPin,
  Palette,
  Save,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  XCircle,
  DollarSign,
  Plus,
  Trash2,
  Edit,
  Loader2,
  Car,
  Landmark,
  Building2,
  Smartphone,
  AlertTriangle,
  ShieldAlert,
  BookOpen,
  Download,
  FileText
} from "lucide-react";

export default function Configuracion() {
  const { user, isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapsStatus, setMapsStatus] = useState(null);
  const [testingMaps, setTestingMaps] = useState(false);
  
  // Purge data state (Admin only)
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState("");
  const [purgeConfirmCheckbox, setPurgeConfirmCheckbox] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [purgePreview, setPurgePreview] = useState(null);
  const [loadingPurgePreview, setLoadingPurgePreview] = useState(false);
  
  // Super Admin manual deletion state
  const [isSuperAdminEmail, setIsSuperAdminEmail] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ collection: "", recordId: "", reason: "" });
  const [deleting, setDeleting] = useState(false);
  const [deletionLogs, setDeletionLogs] = useState([]);
  const [loadingDeletionLogs, setLoadingDeletionLogs] = useState(false);
  
  // User Manuals state
  const [manuals, setManuals] = useState([]);
  const [loadingManuals, setLoadingManuals] = useState(false);
  const [downloadingManual, setDownloadingManual] = useState(null);
  const [generatingManuals, setGeneratingManuals] = useState(false);
  
  // Vehicle rates state
  const [vehicleRates, setVehicleRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(true);
  const [editingRate, setEditingRate] = useState(null);
  const [newRate, setNewRate] = useState({
    vehicle_type: "",
    name: "",
    per_km_rate: 0,
    per_day_rate: 0,
    min_distance_km: 0
  });
  const [showNewRateForm, setShowNewRateForm] = useState(false);
  
  // Bank accounts and terminals state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cardTerminals, setCardTerminals] = useState([]);
  const [loadingFinances, setLoadingFinances] = useState(true);
  const [showBankForm, setShowBankForm] = useState(false);
  const [showTerminalForm, setShowTerminalForm] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [editingTerminal, setEditingTerminal] = useState(null);
  const [newBankAccount, setNewBankAccount] = useState({
    name: "",
    bank_name: "",
    account_holder: "",
    account_number_last4: "",
    clabe_last4: "",
    currency: "MXN",
    notes: ""
  });
  const [newTerminal, setNewTerminal] = useState({
    name: "",
    provider: "",
    terminal_identifier: "",
    currency: "MXN",
    notes: ""
  });
  
  const [formData, setFormData] = useState({
    telegram_bot_token: "",
    telegram_chat_ids: { admin: "", logistica: "" },
    google_maps_api_key: "",
    vendor_edit_window_hours: 24,
    commission_on_payment: true,
    brand_settings: {
      primary_color: "#0F172A",
      secondary_color: "#F1F5F9",
      accent_color: "#2563EB"
    }
  });

  useEffect(() => {
    fetchSettings();
    fetchMapsStatus();
    fetchVehicleRates();
    fetchFinanceData();
    fetchManuals();
    if (isAdmin) {
      checkSuperAdminStatus();
    }
  }, [isAdmin]);

  // Check frontend key locally (it's in this process's env)
  const frontendKeyLoaded = Boolean(process.env.REACT_APP_GOOGLE_MAPS_FRONTEND_KEY);
  const frontendKeyHint = frontendKeyLoaded && process.env.REACT_APP_GOOGLE_MAPS_FRONTEND_KEY.length > 4
    ? `...${process.env.REACT_APP_GOOGLE_MAPS_FRONTEND_KEY.slice(-4)}`
    : null;

  const fetchVehicleRates = async () => {
    setLoadingRates(true);
    try {
      const res = await pricingAPI.getVehicleRates();
      setVehicleRates(res.data);
    } catch (err) {
      console.error("Error fetching vehicle rates:", err);
    } finally {
      setLoadingRates(false);
    }
  };

  const handleSeedRates = async () => {
    try {
      const res = await pricingAPI.seedRates();
      if (res.data.seeded) {
        toast.success("Tarifas de ejemplo creadas");
        fetchVehicleRates();
      } else {
        toast.info(res.data.message);
      }
    } catch (err) {
      toast.error("Error al crear tarifas de ejemplo");
    }
  };

  const handleCreateRate = async () => {
    if (!newRate.vehicle_type || !newRate.name) {
      toast.error("Complete los campos requeridos");
      return;
    }
    try {
      await pricingAPI.createVehicleRate(newRate);
      toast.success("Tarifa creada");
      setShowNewRateForm(false);
      setNewRate({ vehicle_type: "", name: "", per_km_rate: 0, per_day_rate: 0, min_distance_km: 0 });
      fetchVehicleRates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear tarifa");
    }
  };

  const handleUpdateRate = async (rateId) => {
    try {
      await pricingAPI.updateVehicleRate(rateId, editingRate);
      toast.success("Tarifa actualizada");
      setEditingRate(null);
      fetchVehicleRates();
    } catch (err) {
      toast.error("Error al actualizar tarifa");
    }
  };

  const handleDeleteRate = async (rateId) => {
    if (!window.confirm("¿Desactivar esta tarifa?")) return;
    try {
      await pricingAPI.deleteVehicleRate(rateId);
      toast.success("Tarifa desactivada");
      fetchVehicleRates();
    } catch (err) {
      toast.error("Error al desactivar tarifa");
    }
  };

  const fetchMapsStatus = async () => {
    try {
      const res = await mapsAPI.getStatus();
      setMapsStatus(res.data);
    } catch (err) {
      console.error("Error fetching maps status:", err);
    }
  };

  const handleTestMaps = async () => {
    setTestingMaps(true);
    try {
      const res = await mapsAPI.testConnectivity();
      if (res.data.overall === "OK") {
        toast.success("Google Maps funcionando correctamente");
      } else if (res.data.overall === "FAILED") {
        toast.error(res.data.message || "Google Maps no configurado");
      } else {
        toast.warning(res.data.message || "Algunos servicios presentan problemas");
      }
      // Refresh status after test
      await fetchMapsStatus();
    } catch (err) {
      toast.error("Error al probar Google Maps");
    } finally {
      setTestingMaps(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
      setFormData({
        telegram_bot_token: response.data.telegram_bot_token || "",
        telegram_chat_ids: response.data.telegram_chat_ids || { admin: "", logistica: "" },
        google_maps_api_key: response.data.google_maps_api_key || "",
        vendor_edit_window_hours: response.data.vendor_edit_window_hours || 24,
        commission_on_payment: response.data.commission_on_payment !== false,
        brand_settings: response.data.brand_settings || {
          primary_color: "#0F172A",
          secondary_color: "#F1F5F9",
          accent_color: "#2563EB"
        }
      });
    } catch (error) {
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(formData);
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotifications = async () => {
    try {
      const response = await fetch(`${window.location.origin}/api/notifications/test`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json"
        }
      });
      const data = await response.json();
      toast.success(`Telegram: ${data.telegram}, Email: ${data.email}`);
    } catch (error) {
      toast.error("Error en prueba de notificaciones");
    }
  };

  // ==================== ADMIN PURGE DATA ====================
  
  const fetchPurgePreview = async () => {
    setLoadingPurgePreview(true);
    try {
      const response = await adminAPI.purgePreview();
      setPurgePreview(response.data);
    } catch (error) {
      console.error("Error al obtener vista previa:", error);
      toast.error("Error al cargar vista previa de datos");
    } finally {
      setLoadingPurgePreview(false);
    }
  };

  const handleOpenPurgeModal = () => {
    fetchPurgePreview();
    setShowPurgeModal(true);
  };

  const handlePurgeTestData = async () => {
    if (!purgeConfirmCheckbox) {
      toast.error("Debe marcar la casilla de confirmación");
      return;
    }
    if (purgeConfirmText !== "CONFIRMAR") {
      toast.error("Debe escribir 'CONFIRMAR' exactamente para proceder");
      return;
    }
    
    setPurging(true);
    try {
      const response = await adminAPI.purgeTestData();
      setPurgeResult(response.data);
      toast.success(`Datos eliminados correctamente: ${response.data.summary.total_records_deleted} registros`);
      // Reset form
      setPurgeConfirmText("");
      setPurgeConfirmCheckbox(false);
    } catch (error) {
      console.error("Error al purgar datos:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar datos");
    } finally {
      setPurging(false);
    }
  };

  const closePurgeModal = () => {
    setShowPurgeModal(false);
    setPurgeConfirmText("");
    setPurgeConfirmCheckbox(false);
    setPurgeResult(null);
    setPurgePreview(null);
  };

  // ==================== SUPER ADMIN MANUAL DELETION ====================
  
  const checkSuperAdminStatus = async () => {
    try {
      const response = await adminAPI.checkSuperAdmin();
      setIsSuperAdminEmail(response.data.is_super_admin_email);
    } catch (error) {
      setIsSuperAdminEmail(false);
    }
  };

  const fetchDeletionLogs = async () => {
    setLoadingDeletionLogs(true);
    try {
      const response = await adminAPI.getDeletionLogs(50);
      setDeletionLogs(response.data);
    } catch (error) {
      console.error("Error al obtener logs de eliminación:", error);
    } finally {
      setLoadingDeletionLogs(false);
    }
  };

  const handleManualDelete = async () => {
    if (!deleteTarget.collection || !deleteTarget.recordId) {
      toast.error("Debe especificar colección e ID del registro");
      return;
    }
    if (!deleteTarget.reason || deleteTarget.reason.length < 10) {
      toast.error("Debe proporcionar un motivo de al menos 10 caracteres");
      return;
    }
    
    setDeleting(true);
    try {
      const response = await adminAPI.deleteRecord(
        deleteTarget.collection,
        deleteTarget.recordId,
        deleteTarget.reason
      );
      toast.success(response.data.message);
      
      // Show cascade info if any
      const cascade = response.data.cascade_deleted;
      if (cascade && Object.keys(cascade).length > 0) {
        const cascadeInfo = Object.entries(cascade)
          .filter(([_, count]) => count > 0)
          .map(([col, count]) => `${col}: ${count}`)
          .join(", ");
        if (cascadeInfo) {
          toast.info(`Registros relacionados eliminados: ${cascadeInfo}`);
        }
      }
      
      setShowDeleteModal(false);
      setDeleteTarget({ collection: "", recordId: "", reason: "" });
      fetchDeletionLogs();
    } catch (error) {
      console.error("Error al eliminar registro:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar registro");
    } finally {
      setDeleting(false);
    }
  };

  // ==================== USER MANUALS ====================
  
  const fetchManuals = async () => {
    setLoadingManuals(true);
    try {
      const response = await manualsAPI.list();
      setManuals(response.data);
    } catch (error) {
      console.error("Error cargando manuales:", error);
    } finally {
      setLoadingManuals(false);
    }
  };

  const handleDownloadManual = async (manualId, filename) => {
    setDownloadingManual(manualId);
    try {
      const response = await manualsAPI.download(manualId);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Manual descargado: ${filename}`);
    } catch (error) {
      console.error("Error descargando manual:", error);
      toast.error("Error al descargar el manual");
    } finally {
      setDownloadingManual(null);
    }
  };

  const handleGenerateAllManuals = async () => {
    setGeneratingManuals(true);
    try {
      const response = await manualsAPI.generateAll();
      toast.success(response.data.message);
      fetchManuals();
    } catch (error) {
      console.error("Error generando manuales:", error);
      toast.error("Error al generar los manuales");
    } finally {
      setGeneratingManuals(false);
    }
  };

  // ==================== FINANCES (Bank Accounts & Terminals) ====================
  
  const fetchFinanceData = async () => {
    setLoadingFinances(true);
    try {
      const [bankRes, terminalRes] = await Promise.all([
        bankAccountsAPI.getAll({ active_only: false }),
        cardTerminalsAPI.getAll({ active_only: false })
      ]);
      setBankAccounts(bankRes.data);
      setCardTerminals(terminalRes.data);
    } catch (error) {
      console.error("Error fetching finance data:", error);
    } finally {
      setLoadingFinances(false);
    }
  };

  const handleSaveBankAccount = async () => {
    try {
      if (editingBank) {
        await bankAccountsAPI.update(editingBank.id, newBankAccount);
        toast.success("Cuenta bancaria actualizada");
      } else {
        await bankAccountsAPI.create(newBankAccount);
        toast.success("Cuenta bancaria creada");
      }
      setShowBankForm(false);
      setEditingBank(null);
      setNewBankAccount({
        name: "",
        bank_name: "",
        account_holder: "",
        account_number_last4: "",
        clabe_last4: "",
        currency: "MXN",
        notes: ""
      });
      fetchFinanceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar cuenta bancaria");
    }
  };

  const handleDeleteBankAccount = async (id) => {
    if (!window.confirm("¿Desactivar esta cuenta bancaria?")) return;
    try {
      await bankAccountsAPI.delete(id);
      toast.success("Cuenta bancaria desactivada");
      fetchFinanceData();
    } catch (error) {
      toast.error("Error al desactivar cuenta");
    }
  };

  const handleEditBankAccount = (account) => {
    setEditingBank(account);
    setNewBankAccount({
      name: account.name,
      bank_name: account.bank_name,
      account_holder: account.account_holder,
      account_number_last4: account.account_number_last4,
      clabe_last4: account.clabe_last4 || "",
      currency: account.currency,
      notes: account.notes || ""
    });
    setShowBankForm(true);
  };

  const handleSaveTerminal = async () => {
    try {
      if (editingTerminal) {
        await cardTerminalsAPI.update(editingTerminal.id, newTerminal);
        toast.success("Terminal actualizada");
      } else {
        await cardTerminalsAPI.create(newTerminal);
        toast.success("Terminal creada");
      }
      setShowTerminalForm(false);
      setEditingTerminal(null);
      setNewTerminal({
        name: "",
        provider: "",
        terminal_identifier: "",
        currency: "MXN",
        notes: ""
      });
      fetchFinanceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar terminal");
    }
  };

  const handleDeleteTerminal = async (id) => {
    if (!window.confirm("¿Desactivar esta terminal?")) return;
    try {
      await cardTerminalsAPI.delete(id);
      toast.success("Terminal desactivada");
      fetchFinanceData();
    } catch (error) {
      toast.error("Error al desactivar terminal");
    }
  };

  const handleEditTerminal = (terminal) => {
    setEditingTerminal(terminal);
    setNewTerminal({
      name: terminal.name,
      provider: terminal.provider,
      terminal_identifier: terminal.terminal_identifier || "",
      currency: terminal.currency,
      notes: terminal.notes || ""
    });
    setShowTerminalForm(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 lg:p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/3"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="configuracion-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Configuración</h1>
            <p className="text-slate-500 mt-1">Ajustes del sistema</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-settings-btn">
            <Save className="w-4 h-4" />
            {saving ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="notifications" className="space-y-6">
          <TabsList className="bg-slate-100 flex-wrap">
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              <MessageSquare className="w-4 h-4 mr-2" />
              Notificaciones
            </TabsTrigger>
            <TabsTrigger value="integrations" data-testid="tab-integrations">
              <CreditCard className="w-4 h-4 mr-2" />
              Integraciones
            </TabsTrigger>
            <TabsTrigger value="business" data-testid="tab-business">
              <Settings className="w-4 h-4 mr-2" />
              Negocio
            </TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing">
              <DollarSign className="w-4 h-4 mr-2" />
              Precios
            </TabsTrigger>
            <TabsTrigger value="finances" data-testid="tab-finances">
              <Landmark className="w-4 h-4 mr-2" />
              Finanzas
            </TabsTrigger>
            <TabsTrigger value="brand" data-testid="tab-brand">
              <Palette className="w-4 h-4 mr-2" />
              Marca
            </TabsTrigger>
            <TabsTrigger value="manuales" data-testid="tab-manuales">
              <BookOpen className="w-4 h-4 mr-2" />
              Manuales
            </TabsTrigger>
            <TabsTrigger value="catalogos" data-testid="tab-catalogos">
              <Car className="w-4 h-4 mr-2" />
              Catálogos
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="sistema" data-testid="tab-sistema" className="text-red-600 data-[state=active]:text-red-700">
                <ShieldAlert className="w-4 h-4 mr-2" />
                Sistema
              </TabsTrigger>
            )}
          </TabsList>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Telegram
                </CardTitle>
                <CardDescription>
                  Configura las notificaciones por Telegram
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="telegram_bot_token">Bot Token</Label>
                  <Input
                    id="telegram_bot_token"
                    type="password"
                    value={formData.telegram_bot_token}
                    onChange={(e) => setFormData(prev => ({ ...prev, telegram_bot_token: e.target.value }))}
                    placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                    data-testid="telegram-token-input"
                  />
                  <p className="text-sm text-slate-500">
                    Obtén tu token de @BotFather en Telegram
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Chat ID Admin</Label>
                    <Input
                      value={formData.telegram_chat_ids?.admin || ""}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        telegram_chat_ids: { ...prev.telegram_chat_ids, admin: e.target.value }
                      }))}
                      placeholder="-100123456789"
                      data-testid="telegram-admin-chat-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Chat ID Logística</Label>
                    <Input
                      value={formData.telegram_chat_ids?.logistica || ""}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        telegram_chat_ids: { ...prev.telegram_chat_ids, logistica: e.target.value }
                      }))}
                      placeholder="-100123456789"
                      data-testid="telegram-logistica-chat-input"
                    />
                  </div>
                </div>
                
                {!formData.telegram_bot_token && (
                  <div className="flex items-center gap-2 p-4 bg-amber-50 rounded-lg text-amber-800">
                    <AlertCircle className="w-5 h-5" />
                    <span className="text-sm">Integración pendiente - Configura el Bot Token para activar</span>
                  </div>
                )}

                <Button type="button" variant="outline" onClick={handleTestNotifications} data-testid="test-notifications-btn">
                  Probar Notificaciones
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Google Maps
                </CardTitle>
                <CardDescription>
                  API para autocomplete de direcciones y cálculo de distancias
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Maps Status Display */}
                {mapsStatus && (
                  <div className={`p-4 rounded-lg border ${(mapsStatus.available && frontendKeyLoaded) ? 'bg-green-50 border-green-200' : mapsStatus.available ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {(mapsStatus.available && frontendKeyLoaded) ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : mapsStatus.available ? (
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-amber-600" />
                        )}
                        <span className={`font-medium ${(mapsStatus.available && frontendKeyLoaded) ? 'text-green-800' : mapsStatus.available ? 'text-blue-800' : 'text-amber-800'}`}>
                          {(mapsStatus.available && frontendKeyLoaded) 
                            ? "Completo - Todas las funciones activas"
                            : mapsStatus.available 
                              ? "Parcial - Backend OK, Frontend pendiente"
                              : mapsStatus.message}
                        </span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleTestMaps}
                        disabled={testingMaps}
                        className="gap-2"
                        data-testid="test-maps-btn"
                      >
                        <RefreshCw className={`w-4 h-4 ${testingMaps ? 'animate-spin' : ''}`} />
                        Probar Backend
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {/* Backend Key - from API */}
                      <div className="flex items-center gap-2">
                        {mapsStatus.backendKeyLoaded ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-slate-400" />}
                        <span className={mapsStatus.backendKeyLoaded ? 'text-green-700' : 'text-slate-500'}>
                          Backend Key {mapsStatus.backendKeyHint || '(no configurada)'}
                        </span>
                      </div>
                      {/* Frontend Key - local check */}
                      <div className="flex items-center gap-2">
                        {frontendKeyLoaded ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-slate-400" />}
                        <span className={frontendKeyLoaded ? 'text-green-700' : 'text-slate-500'}>
                          Frontend Key {frontendKeyHint || '(no configurada)'}
                        </span>
                      </div>
                      {/* Places - uses frontend key */}
                      <div className="flex items-center gap-2">
                        {frontendKeyLoaded ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-slate-400" />}
                        <span className={frontendKeyLoaded ? 'text-green-700' : 'text-slate-500'}>Places Autocomplete</span>
                      </div>
                      {/* Geocoding - uses backend key */}
                      <div className="flex items-center gap-2">
                        {mapsStatus.geocodingEnabled ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-slate-400" />}
                        <span className={mapsStatus.geocodingEnabled ? 'text-green-700' : 'text-slate-500'}>Geocoding</span>
                      </div>
                      {/* Distance Matrix - uses backend key */}
                      <div className="flex items-center gap-2">
                        {mapsStatus.distanceMatrixEnabled ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-slate-400" />}
                        <span className={mapsStatus.distanceMatrixEnabled ? 'text-green-700' : 'text-slate-500'}>Distance Matrix</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Info about key configuration */}
                <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-600 space-y-2">
                  <p className="font-medium text-slate-700">Arquitectura de claves:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li><strong>Backend Key:</strong> Geocoding + Distance Matrix (servidor) - Restricción IP recomendada</li>
                    <li><strong>Frontend Key:</strong> Places Autocomplete (navegador) - Restricción HTTP Referrer recomendada</li>
                  </ul>
                  <p className="text-slate-500 mt-2">
                    Sin claves configuradas, el sistema permite entrada manual de direcciones.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Stripe
                </CardTitle>
                <CardDescription>
                  Pagos en línea para clientes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 p-4 bg-green-50 rounded-lg text-green-800">
                  <Settings className="w-5 h-5" />
                  <span className="text-sm">Stripe está configurado con las claves del sistema</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Business Tab */}
          <TabsContent value="business" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Parámetros de Negocio</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="vendor_edit_window">Ventana de Edición para Vendedores (horas)</Label>
                  <Input
                    id="vendor_edit_window"
                    type="number"
                    min="1"
                    max="168"
                    value={formData.vendor_edit_window_hours}
                    onChange={(e) => setFormData(prev => ({ ...prev, vendor_edit_window_hours: parseInt(e.target.value) }))}
                    className="max-w-xs"
                    data-testid="edit-window-input"
                  />
                  <p className="text-sm text-slate-500">
                    Tiempo máximo que un vendedor puede editar un contrato después de crearlo
                  </p>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Comisión al recibir pago</p>
                    <p className="text-sm text-slate-500">
                      Las comisiones se calculan cuando se registra un pago
                    </p>
                  </div>
                  <Switch
                    checked={formData.commission_on_payment}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, commission_on_payment: checked }))}
                    data-testid="commission-switch"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <Car className="w-5 h-5" />
                      Tarifas por Tipo de Vehículo
                    </CardTitle>
                    <CardDescription>
                      Configure las tarifas base y por kilómetro para cada tipo de vehículo. El motor de precios usará estas tarifas para sugerir precios en cotizaciones.
                    </CardDescription>
                  </div>
                  {vehicleRates.length === 0 && (
                    <Button variant="outline" onClick={handleSeedRates} data-testid="seed-rates-btn">
                      Crear Tarifas de Ejemplo
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {loadingRates ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : vehicleRates.length === 0 ? (
                  <div className="text-center p-8 bg-slate-50 rounded-lg">
                    <Car className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No hay tarifas configuradas</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Cree tarifas de ejemplo o agregue una nueva tarifa manualmente
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Nombre</TableHead>
                        <TableHead className="text-right">$/km</TableHead>
                        <TableHead className="text-right">$/día</TableHead>
                        <TableHead className="text-right">Km Mín.</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vehicleRates.map(rate => (
                        <TableRow key={rate.id}>
                          {editingRate?.id === rate.id ? (
                            <>
                              <TableCell>
                                <Input
                                  value={editingRate.vehicle_type}
                                  onChange={e => setEditingRate({...editingRate, vehicle_type: e.target.value})}
                                  className="w-24"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={editingRate.name}
                                  onChange={e => setEditingRate({...editingRate, name: e.target.value})}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={editingRate.per_km_rate}
                                  onChange={e => setEditingRate({...editingRate, per_km_rate: parseFloat(e.target.value) || 0})}
                                  className="w-20 text-right"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={editingRate.per_day_rate || 0}
                                  onChange={e => setEditingRate({...editingRate, per_day_rate: parseFloat(e.target.value) || 0})}
                                  className="w-24 text-right"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={editingRate.min_distance_km}
                                  onChange={e => setEditingRate({...editingRate, min_distance_km: parseFloat(e.target.value) || 0})}
                                  className="w-20 text-right"
                                />
                              </TableCell>
                              <TableCell className="text-right">
                                <Button size="sm" onClick={() => handleUpdateRate(rate.id)} className="mr-2">
                                  Guardar
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingRate(null)}>
                                  Cancelar
                                </Button>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell className="font-mono text-xs uppercase">{rate.vehicle_type}</TableCell>
                              <TableCell className="font-medium">{rate.name}</TableCell>
                              <TableCell className="text-right">${rate.per_km_rate}</TableCell>
                              <TableCell className="text-right">${(rate.per_day_rate || 0).toLocaleString("es-MX")}</TableCell>
                              <TableCell className="text-right">{rate.min_distance_km || 0}</TableCell>
                              <TableCell className="text-right">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => setEditingRate({...rate})}
                                  data-testid={`edit-rate-${rate.vehicle_type}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => handleDeleteRate(rate.id)}
                                  className="text-red-600"
                                  data-testid={`delete-rate-${rate.vehicle_type}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {/* Add new rate form */}
                {showNewRateForm ? (
                  <div className="border rounded-lg p-4 bg-slate-50 space-y-4">
                    <h4 className="font-medium">Nueva Tarifa</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs">Tipo de Vehículo</Label>
                        <Input
                          placeholder="sedan, suv, van..."
                          value={newRate.vehicle_type}
                          onChange={e => setNewRate({...newRate, vehicle_type: e.target.value.toLowerCase()})}
                          data-testid="new-rate-type"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Nombre</Label>
                        <Input
                          placeholder="Sedán (4 pasajeros)"
                          value={newRate.name}
                          onChange={e => setNewRate({...newRate, name: e.target.value})}
                          data-testid="new-rate-name"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Por Km ($)</Label>
                        <Input
                          type="number"
                          value={newRate.per_km_rate || ""}
                          onChange={e => setNewRate({...newRate, per_km_rate: parseFloat(e.target.value) || 0})}
                          data-testid="new-rate-km"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Por Día ($)</Label>
                        <Input
                          type="number"
                          value={newRate.per_day_rate || ""}
                          onChange={e => setNewRate({...newRate, per_day_rate: parseFloat(e.target.value) || 0})}
                          data-testid="new-rate-day"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Km Mínimo</Label>
                        <Input
                          type="number"
                          value={newRate.min_distance_km || ""}
                          onChange={e => setNewRate({...newRate, min_distance_km: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleCreateRate} data-testid="save-new-rate-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Crear Tarifa
                      </Button>
                      <Button variant="ghost" onClick={() => setShowNewRateForm(false)}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowNewRateForm(true)}
                    data-testid="add-rate-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Tarifa
                  </Button>
                )}

                {/* Pricing explanation */}
                <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-800 mt-4">
                  <p className="font-medium mb-2">¿Cómo funciona el motor de precios?</p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-700">
                    <li><strong>Rutas Fijas:</strong> Si existe una ruta fija que coincida, se usa ese precio (prioridad)</li>
                    <li><strong>Cálculo por Distancia y Días:</strong> Precio = (km × $/km) + (días × $/día)</li>
                    <li><strong>Km Mínimo:</strong> Si la distancia es menor al mínimo, se cobra el mínimo</li>
                    <li><strong>Manual:</strong> El vendedor siempre puede modificar el precio final</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Finances Tab - Bank Accounts & Terminals */}
          <TabsContent value="finances" className="space-y-6">
            {/* Bank Accounts */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Cuentas Bancarias
                    </CardTitle>
                    <CardDescription>
                      Configura las cuentas para recibir transferencias
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => {
                      setEditingBank(null);
                      setNewBankAccount({
                        name: "",
                        bank_name: "",
                        account_holder: "",
                        account_number_last4: "",
                        clabe_last4: "",
                        currency: "MXN",
                        notes: ""
                      });
                      setShowBankForm(true);
                    }}
                    size="sm"
                    className="gap-2"
                    data-testid="add-bank-account-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Nueva Cuenta
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showBankForm && (
                  <div className="mb-6 p-4 bg-slate-50 rounded-lg border space-y-4">
                    <h4 className="font-medium">{editingBank ? "Editar Cuenta" : "Nueva Cuenta Bancaria"}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nombre *</Label>
                        <Input
                          value={newBankAccount.name}
                          onChange={(e) => setNewBankAccount(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="BBVA Principal"
                          data-testid="bank-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Banco *</Label>
                        <Input
                          value={newBankAccount.bank_name}
                          onChange={(e) => setNewBankAccount(prev => ({ ...prev, bank_name: e.target.value }))}
                          placeholder="BBVA, Banorte, Santander..."
                          data-testid="bank-bank-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Titular *</Label>
                        <Input
                          value={newBankAccount.account_holder}
                          onChange={(e) => setNewBankAccount(prev => ({ ...prev, account_holder: e.target.value }))}
                          placeholder="Razón social"
                          data-testid="bank-holder-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Últimos 4 dígitos cuenta *</Label>
                        <Input
                          value={newBankAccount.account_number_last4}
                          onChange={(e) => setNewBankAccount(prev => ({ ...prev, account_number_last4: e.target.value.slice(0, 4) }))}
                          placeholder="1234"
                          maxLength={4}
                          data-testid="bank-last4-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Últimos 4 CLABE</Label>
                        <Input
                          value={newBankAccount.clabe_last4}
                          onChange={(e) => setNewBankAccount(prev => ({ ...prev, clabe_last4: e.target.value.slice(0, 4) }))}
                          placeholder="5678"
                          maxLength={4}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Moneda</Label>
                        <select
                          value={newBankAccount.currency}
                          onChange={(e) => setNewBankAccount(prev => ({ ...prev, currency: e.target.value }))}
                          className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                          data-testid="bank-currency-select"
                        >
                          <option value="MXN">🇲🇽 Solo MXN</option>
                          <option value="USD">🇺🇸 Solo USD</option>
                          <option value="BOTH">🌐 Ambas</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Notas</Label>
                      <Input
                        value={newBankAccount.notes}
                        onChange={(e) => setNewBankAccount(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Notas adicionales..."
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => { setShowBankForm(false); setEditingBank(null); }}>
                        Cancelar
                      </Button>
                      <Button onClick={handleSaveBankAccount} data-testid="save-bank-btn">
                        {editingBank ? "Actualizar" : "Guardar"}
                      </Button>
                    </div>
                  </div>
                )}

                {loadingFinances ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : bankAccounts.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No hay cuentas bancarias configuradas</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Banco</TableHead>
                        <TableHead>Titular</TableHead>
                        <TableHead>Cuenta</TableHead>
                        <TableHead>Moneda</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bankAccounts.map((account) => (
                        <TableRow key={account.id} className={!account.is_active ? "opacity-50" : ""}>
                          <TableCell className="font-medium">{account.name}</TableCell>
                          <TableCell>{account.bank_name}</TableCell>
                          <TableCell className="text-sm">{account.account_holder}</TableCell>
                          <TableCell className="font-mono text-sm">****{account.account_number_last4}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${
                              account.currency === "BOTH" ? "bg-purple-100 text-purple-800" :
                              account.currency === "USD" ? "bg-green-100 text-green-800" :
                              "bg-blue-100 text-blue-800"
                            }`}>
                              {account.currency}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${
                              account.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                            }`}>
                              {account.is_active ? "Activa" : "Inactiva"}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditBankAccount(account)}
                                data-testid={`edit-bank-${account.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {account.is_active && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteBankAccount(account.id)}
                                  data-testid={`delete-bank-${account.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Card Terminals */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <Smartphone className="w-5 h-5" />
                      Terminales de Cobro
                    </CardTitle>
                    <CardDescription>
                      Configura las terminales para cobros con tarjeta
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => {
                      setEditingTerminal(null);
                      setNewTerminal({
                        name: "",
                        provider: "",
                        terminal_identifier: "",
                        currency: "MXN",
                        notes: ""
                      });
                      setShowTerminalForm(true);
                    }}
                    size="sm"
                    className="gap-2"
                    data-testid="add-terminal-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Nueva Terminal
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showTerminalForm && (
                  <div className="mb-6 p-4 bg-slate-50 rounded-lg border space-y-4">
                    <h4 className="font-medium">{editingTerminal ? "Editar Terminal" : "Nueva Terminal"}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nombre *</Label>
                        <Input
                          value={newTerminal.name}
                          onChange={(e) => setNewTerminal(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Terminal Clip #1"
                          data-testid="terminal-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Proveedor *</Label>
                        <Input
                          value={newTerminal.provider}
                          onChange={(e) => setNewTerminal(prev => ({ ...prev, provider: e.target.value }))}
                          placeholder="Clip, BBVA, Sr. Pago..."
                          data-testid="terminal-provider-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Identificador</Label>
                        <Input
                          value={newTerminal.terminal_identifier}
                          onChange={(e) => setNewTerminal(prev => ({ ...prev, terminal_identifier: e.target.value }))}
                          placeholder="Alias o número"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Moneda</Label>
                        <select
                          value={newTerminal.currency}
                          onChange={(e) => setNewTerminal(prev => ({ ...prev, currency: e.target.value }))}
                          className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                          data-testid="terminal-currency-select"
                        >
                          <option value="MXN">🇲🇽 Solo MXN</option>
                          <option value="USD">🇺🇸 Solo USD</option>
                          <option value="BOTH">🌐 Ambas</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Notas</Label>
                      <Input
                        value={newTerminal.notes}
                        onChange={(e) => setNewTerminal(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Notas adicionales..."
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => { setShowTerminalForm(false); setEditingTerminal(null); }}>
                        Cancelar
                      </Button>
                      <Button onClick={handleSaveTerminal} data-testid="save-terminal-btn">
                        {editingTerminal ? "Actualizar" : "Guardar"}
                      </Button>
                    </div>
                  </div>
                )}

                {loadingFinances ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : cardTerminals.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No hay terminales configuradas</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Proveedor</TableHead>
                        <TableHead>Identificador</TableHead>
                        <TableHead>Moneda</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cardTerminals.map((terminal) => (
                        <TableRow key={terminal.id} className={!terminal.is_active ? "opacity-50" : ""}>
                          <TableCell className="font-medium">{terminal.name}</TableCell>
                          <TableCell>{terminal.provider}</TableCell>
                          <TableCell className="font-mono text-sm">{terminal.terminal_identifier || "—"}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${
                              terminal.currency === "BOTH" ? "bg-purple-100 text-purple-800" :
                              terminal.currency === "USD" ? "bg-green-100 text-green-800" :
                              "bg-blue-100 text-blue-800"
                            }`}>
                              {terminal.currency}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${
                              terminal.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                            }`}>
                              {terminal.is_active ? "Activa" : "Inactiva"}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditTerminal(terminal)}
                                data-testid={`edit-terminal-${terminal.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {terminal.is_active && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteTerminal(terminal.id)}
                                  data-testid={`delete-terminal-${terminal.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Brand Tab */}
          <TabsContent value="brand" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Colores de Marca
                </CardTitle>
                <CardDescription>
                  Personaliza los colores del sistema
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label>Color Primario</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="color"
                        value={formData.brand_settings?.primary_color || "#0F172A"}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          brand_settings: { ...prev.brand_settings, primary_color: e.target.value }
                        }))}
                        className="w-12 h-12 p-1 cursor-pointer"
                        data-testid="primary-color-input"
                      />
                      <Input
                        value={formData.brand_settings?.primary_color || "#0F172A"}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          brand_settings: { ...prev.brand_settings, primary_color: e.target.value }
                        }))}
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Color Secundario</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="color"
                        value={formData.brand_settings?.secondary_color || "#F1F5F9"}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          brand_settings: { ...prev.brand_settings, secondary_color: e.target.value }
                        }))}
                        className="w-12 h-12 p-1 cursor-pointer"
                        data-testid="secondary-color-input"
                      />
                      <Input
                        value={formData.brand_settings?.secondary_color || "#F1F5F9"}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          brand_settings: { ...prev.brand_settings, secondary_color: e.target.value }
                        }))}
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Color de Acento</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="color"
                        value={formData.brand_settings?.accent_color || "#2563EB"}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          brand_settings: { ...prev.brand_settings, accent_color: e.target.value }
                        }))}
                        className="w-12 h-12 p-1 cursor-pointer"
                        data-testid="accent-color-input"
                      />
                      <Input
                        value={formData.brand_settings?.accent_color || "#2563EB"}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          brand_settings: { ...prev.brand_settings, accent_color: e.target.value }
                        }))}
                        className="font-mono"
                      />
                    </div>
                  </div>
                </div>

                <div className="p-6 border rounded-lg">
                  <h4 className="font-medium mb-4">Vista Previa</h4>
                  <div className="flex gap-4">
                    <button
                      className="px-4 py-2 rounded-md text-white font-medium"
                      style={{ backgroundColor: formData.brand_settings?.primary_color }}
                    >
                      Botón Primario
                    </button>
                    <button
                      className="px-4 py-2 rounded-md font-medium border"
                      style={{ 
                        backgroundColor: formData.brand_settings?.secondary_color,
                        borderColor: formData.brand_settings?.primary_color
                      }}
                    >
                      Botón Secundario
                    </button>
                    <button
                      className="px-4 py-2 rounded-md text-white font-medium"
                      style={{ backgroundColor: formData.brand_settings?.accent_color }}
                    >
                      Botón Acento
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Manuales Tab - User Documentation */}
          <TabsContent value="manuales" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                      Manuales de Usuario
                    </CardTitle>
                    <CardDescription>
                      Documentación oficial del CRM TenaTour para cada perfil de usuario.
                    </CardDescription>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateAllManuals}
                      disabled={generatingManuals}
                      className="gap-2"
                      data-testid="generate-all-manuals-btn"
                    >
                      {generatingManuals ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generando...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          Regenerar Todos
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loadingManuals ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : manuals.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay manuales disponibles para su perfil.</p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {manuals.map((manual) => (
                      <div
                        key={manual.id}
                        className="p-4 border rounded-lg bg-slate-50/50 hover:bg-slate-100/80 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <FileText className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-slate-900 truncate">
                              {manual.title}
                            </h4>
                            <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                              {manual.description}
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-3 gap-2"
                              onClick={() => handleDownloadManual(manual.id, manual.filename)}
                              disabled={downloadingManual === manual.id}
                              data-testid={`download-manual-${manual.id}`}
                            >
                              {downloadingManual === manual.id ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Generando PDF...
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4" />
                                  Descargar PDF
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <h4 className="font-medium text-blue-800 mb-2">Acerca de los Manuales</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Los manuales se generan en formato PDF profesional</li>
                    <li>• Cada perfil tiene acceso solo a sus manuales correspondientes</li>
                    <li>• Los documentos incluyen la versión actual del sistema (v3.4.0)</li>
                    <li>• Contenido: índice, guías por módulo, buenas prácticas y FAQ</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Catalogs Tab */}
          <TabsContent value="catalogos" className="space-y-6">
            <VehicleTypesManager />
            <ServiceTypesManager />
            <ExpenseTypesManager />
          </TabsContent>

          {/* System Tab - Admin Only - Danger Zone */}
          {isAdmin && (
            <TabsContent value="sistema" className="space-y-6">
              {/* Tool A: Purge All Data */}
              <Card className="border-red-200 bg-red-50/30">
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Zona de Peligro - Purga Total
                  </CardTitle>
                  <CardDescription className="text-red-600">
                    Eliminar TODOS los datos operativos/transaccionales del sistema.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-6 border-2 border-red-300 rounded-lg bg-white">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-red-100 rounded-full">
                        <Trash2 className="w-6 h-6 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-red-800">
                          Purgar Todos los Datos Operativos
                        </h3>
                        <p className="text-sm text-slate-600 mt-1 mb-3">
                          Esta acción eliminará <strong>permanentemente</strong> todos los registros transaccionales:
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm mb-4">
                          <span className="text-red-700">• Cotizaciones</span>
                          <span className="text-red-700">• Contratos</span>
                          <span className="text-red-700">• Contratos Marco</span>
                          <span className="text-red-700">• Clientes</span>
                          <span className="text-red-700">• Calendario/Eventos</span>
                          <span className="text-red-700">• Pagos</span>
                          <span className="text-red-700">• Transacciones</span>
                          <span className="text-red-700">• Cuentas por Cobrar</span>
                          <span className="text-red-700">• Cuentas por Pagar</span>
                          <span className="text-red-700">• Comisiones</span>
                          <span className="text-red-700">• Libro Contable (Ingresos)</span>
                          <span className="text-red-700">• Mantenimiento/Gastos</span>
                          <span className="text-red-700">• Alertas</span>
                          <span className="text-red-700">• Bitácoras</span>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200 mb-4">
                          <p className="text-sm text-green-800 font-medium mb-1">
                            ✓ Los siguientes datos se conservarán:
                          </p>
                          <p className="text-xs text-green-700">
                            Usuarios, vehículos, choferes, tarifas, configuración, cuentas bancarias, terminales, plantillas PDF
                          </p>
                        </div>
                        <Button 
                          variant="destructive" 
                          onClick={handleOpenPurgeModal}
                          className="gap-2"
                          data-testid="open-purge-modal-btn"
                        >
                          <AlertTriangle className="w-4 h-4" />
                          Iniciar Purga Total
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tool B: Manual Individual Deletion - ONLY for gerencia@tenatourgdl.com */}
              {isSuperAdminEmail && (
                <Card className="border-orange-200 bg-orange-50/30">
                  <CardHeader>
                    <CardTitle className="text-lg font-heading flex items-center gap-2 text-orange-700">
                      <ShieldAlert className="w-5 h-5" />
                      Eliminación Manual de Registros
                    </CardTitle>
                    <CardDescription className="text-orange-600">
                      Función exclusiva para el administrador principal (gerencia@tenatourgdl.com).
                      Permite eliminar registros específicos con bitácora obligatoria.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-white border border-orange-200 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Colección</Label>
                          <select
                            className="w-full px-3 py-2 border rounded-md text-sm"
                            value={deleteTarget.collection}
                            onChange={(e) => setDeleteTarget(prev => ({ ...prev, collection: e.target.value }))}
                          >
                            <option value="">Seleccionar...</option>
                            <option value="quotes">Cotizaciones</option>
                            <option value="contracts">Contratos</option>
                            <option value="contract_frames">Contratos Marco</option>
                            <option value="clients">Clientes</option>
                            <option value="payments">Pagos</option>
                            <option value="accounts_receivable">Cuentas por Cobrar</option>
                            <option value="accounts_payable">Cuentas por Pagar</option>
                            <option value="commissions">Comisiones</option>
                            <option value="calendar_events">Eventos Calendario</option>
                            <option value="alerts">Alertas</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>ID del Registro</Label>
                          <Input
                            placeholder="ID del registro a eliminar"
                            value={deleteTarget.recordId}
                            onChange={(e) => setDeleteTarget(prev => ({ ...prev, recordId: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Motivo (obligatorio, mín. 10 caracteres)</Label>
                          <Input
                            placeholder="Motivo de la eliminación..."
                            value={deleteTarget.reason}
                            onChange={(e) => setDeleteTarget(prev => ({ ...prev, reason: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                          <strong>⚠️ Advertencia:</strong> Esta acción puede afectar finanzas, reportes e historial.
                          Se eliminarán también los registros relacionados (pagos, comisiones, etc.).
                        </p>
                      </div>
                      <div className="mt-4 flex gap-2">
                        <Button
                          variant="destructive"
                          onClick={handleManualDelete}
                          disabled={!deleteTarget.collection || !deleteTarget.recordId || deleteTarget.reason.length < 10 || deleting}
                          className="gap-2"
                        >
                          {deleting ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Eliminando...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4" />
                              Eliminar Registro
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            fetchDeletionLogs();
                          }}
                          className="gap-2"
                        >
                          <FileText className="w-4 h-4" />
                          Ver Bitácora de Eliminaciones
                        </Button>
                      </div>
                    </div>

                    {/* Deletion Logs */}
                    {deletionLogs.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-slate-50 px-4 py-2 border-b">
                          <h4 className="font-medium text-sm">Últimas Eliminaciones Manuales</h4>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left">Fecha</th>
                                <th className="px-3 py-2 text-left">Colección</th>
                                <th className="px-3 py-2 text-left">ID</th>
                                <th className="px-3 py-2 text-left">Motivo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {deletionLogs.map((log) => (
                                <tr key={log.id} className="border-t hover:bg-slate-50">
                                  <td className="px-3 py-2 text-xs">
                                    {formatDateTime(log.timestamp)}
                                  </td>
                                  <td className="px-3 py-2 font-mono text-xs">{log.collection}</td>
                                  <td className="px-3 py-2 font-mono text-xs truncate max-w-[100px]">{log.record_id}</td>
                                  <td className="px-3 py-2 text-xs truncate max-w-[200px]">{log.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Purge Confirmation Modal - Enhanced */}
        <Dialog open={showPurgeModal} onOpenChange={setShowPurgeModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Confirmar Purga Total de Datos
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                Esta acción es <strong className="text-red-600">IRREVERSIBLE</strong>. 
                Todos los datos operativos/transaccionales serán eliminados permanentemente.
              </DialogDescription>
            </DialogHeader>
            
            {!purgeResult ? (
              <div className="space-y-4 py-4">
                {/* Preview of data to be deleted */}
                {loadingPurgePreview ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    <span className="ml-2 text-slate-500">Cargando vista previa...</span>
                  </div>
                ) : purgePreview && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-red-50 px-4 py-2 border-b border-red-200">
                      <h4 className="font-medium text-red-800">
                        Vista Previa: {purgePreview.total_records} registros serán eliminados
                      </h4>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">Colección</th>
                            <th className="px-3 py-2 text-right font-medium">Registros</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(purgePreview.collections)
                            .filter(([_, count]) => count > 0)
                            .map(([collection, count]) => (
                              <tr key={collection} className="border-t">
                                <td className="px-3 py-2 font-mono text-xs">{collection}</td>
                                <td className="px-3 py-2 text-right text-red-600 font-medium">{count}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Confirmation steps */}
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="purge-checkbox"
                      checked={purgeConfirmCheckbox}
                      onChange={(e) => setPurgeConfirmCheckbox(e.target.checked)}
                      className="mt-1"
                    />
                    <label htmlFor="purge-checkbox" className="text-sm text-yellow-800 cursor-pointer">
                      <strong>Entiendo que esta acción es irreversible</strong> y que todos los datos 
                      operativos (cotizaciones, contratos, clientes, pagos, etc.) serán eliminados permanentemente.
                    </label>
                  </div>
                  
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 font-medium mb-2">
                      Para confirmar, escriba exactamente: <code className="bg-red-100 px-2 py-1 rounded font-bold">CONFIRMAR</code>
                    </p>
                    <Input
                      value={purgeConfirmText}
                      onChange={(e) => setPurgeConfirmText(e.target.value)}
                      placeholder="CONFIRMAR"
                      className={`font-mono text-center text-lg ${
                        purgeConfirmText === "CONFIRMAR" 
                          ? "border-green-500 bg-green-50" 
                          : purgeConfirmText.length > 0 
                            ? "border-red-300" 
                            : ""
                      }`}
                      data-testid="purge-confirm-input"
                      disabled={purging}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">Purga completada exitosamente</span>
                  </div>
                  <p className="text-sm text-green-700">
                    Se eliminaron <strong>{purgeResult.summary.total_records_deleted}</strong> registros en total.
                  </p>
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Colección</th>
                        <th className="px-3 py-2 text-right font-medium">Antes</th>
                        <th className="px-3 py-2 text-right font-medium">Eliminados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(purgeResult.details.before)
                        .filter(col => purgeResult.details.before[col] > 0 || purgeResult.details.deleted[col] > 0)
                        .map((collection) => (
                          <tr key={collection} className="border-t">
                            <td className="px-3 py-2 font-mono text-xs">{collection}</td>
                            <td className="px-3 py-2 text-right">{purgeResult.details.before[collection]}</td>
                            <td className="px-3 py-2 text-right text-red-600 font-medium">
                              {purgeResult.details.deleted[collection]}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                
                <p className="text-xs text-slate-500 text-center">
                  Ejecutado por: {purgeResult.performed_by} | {formatDateTime(purgeResult.performed_at)}
                </p>
              </div>
            )}
            
            <DialogFooter>
              {!purgeResult ? (
                <>
                  <Button 
                    variant="outline" 
                    onClick={closePurgeModal}
                    disabled={purging}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={handlePurgeTestData}
                    disabled={!purgeConfirmCheckbox || purgeConfirmText !== "CONFIRMAR" || purging}
                    className="gap-2"
                    data-testid="confirm-purge-btn"
                  >
                    {purging ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Eliminando...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Ejecutar Purga Total
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <Button onClick={closePurgeModal} className="w-full">
                  Cerrar
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
